<template>
  <SoPageWithHeader title="Maintenance">
    <SoAlert
      v-if="maintenanceConfig.error"
      title="Failed to load maintenance configuration"
      data-testid="maintenance-error-alert"
    />
    <p class="text-center my-8">
      Here you can enable/disable the Maintenance banner on So Energy web pages.
    </p>
    <div class="d-flex justify-space-between align-center">
      <SoToggle
        v-model="isMaintenanceBannerEnabled"
        :label="`Maintenance Banner ${
          isMaintenanceBannerEnabled ? 'on' : 'off'
        }`"
        class="mb-8"
      />
      <div v-if="hasUnSavedChanges" class="d-flex gap-1">
        <SoButton
          title="Cancel"
          class="mr-2"
          data-testid="maintenance-cancel-btn"
          large
          @click="resetChanges"
        />
        <SoButton
          :loading="maintenanceConfigMutator.isPending"
          :aria-busy="maintenanceConfigMutator.isPending"
          title="Review & Save"
          type="primary"
          data-testid="maintenance-save-btn"
          large
          @click="openSaveModal"
        />
      </div>
    </div>
    <SoTextField v-model="title" label="Title" class="mb-4" />
    <SoTextEditor
      v-model="message"
      :features="['formatting', 'link']"
      label="Message"
      class="mb-8"
    />

    <SoDataTable
      :headers="headers"
      :items="items"
      :items-per-page="100"
      :has-pagination="false"
      :loading="maintenanceConfig.isFetching"
      actions-header-text="Banner Enabled"
      data-testid="maintenance-data-table"
    >
      <template #itemActions="{ item }">
        <SoToggle v-model="item.enabled" class="d-flex flex-column align-end" />
      </template>
    </SoDataTable>

    <AdminMaintenanceModal
      v-model="showSaveModal"
      :is-pending="maintenanceConfigMutator.isPending"
      :is-success="maintenanceConfigMutator.isSuccess"
      :is-error="maintenanceConfigMutator.isError"
      :config-changes="configChanges"
      @save="saveMaintenanceConfig"
    />
  </SoPageWithHeader>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { cloneDeep } from "lodash-es"
  import AdminMaintenanceModal from "./modals/AdminMaintenanceModal.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoPageWithHeader from "@/components/templates/SoPageWithHeader.vue"
  import SoTextEditor from "@/components/SoTextEditor.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoToggle from "@/components/formControls/SoToggle.vue"
  import { MaintenanceService } from "@/services/MaintenanceService"
  import { MaintenanceMutator } from "@/mutators/maintenance/MaintenanceMutator"

  const AVAILABLE_PAGES = [
    "/energy/quote/*",
    "/myaccount/",
    "/myaccount/login",
    "/myaccount/activate-account",
    "/myaccount/set-password",
    "/myaccount/password-reset",
    "/myaccount/password-change",
  ] as const

  export default defineComponent({
    components: {
      AdminMaintenanceModal,
      SoAlert,
      SoButton,
      SoDataTable,
      SoPageWithHeader,
      SoTextEditor,
      SoTextField,
      SoToggle,
    },
    data() {
      return {
        headers: [{ title: "Page Name", key: "name" }],
        isMaintenanceBannerEnabled: false,
        items: [] as Array<{ id: number; name: string; enabled: boolean }>,
        maintenanceConfig: MaintenanceService.getMaintenanceConfig(),
        maintenanceConfigMutator: MaintenanceMutator.updateConfig(),
        message: "",
        showSaveModal: false,
        title: "",
        configCopy: {
          isMaintenanceBannerEnabled: false,
          items: [] as Array<{ id: number; name: string; enabled: boolean }>,
          message: "",
          title: "",
        },
      }
    },
    computed: {
      hasUnSavedChanges() {
        const changes = this.configChanges

        return (
          changes.bannerEnabled.changed ||
          changes.title.changed ||
          changes.message.changed ||
          changes.pages.length > 0
        )
      },
      configChanges() {
        const { isMaintenanceBannerEnabled, title, message, items } =
          this.configCopy

        return {
          bannerEnabled: {
            changed:
              this.isMaintenanceBannerEnabled !== isMaintenanceBannerEnabled,
            from: this.isMaintenanceBannerEnabled,
            to: isMaintenanceBannerEnabled,
          },
          title: {
            changed: this.title !== title,
            from: this.title,
            to: title,
          },
          message: {
            changed: this.message !== message,
            from: this.message,
            to: message,
          },
          pages: this.items.filter((currentItem) => {
            const original = items.find(
              (originalItem) => originalItem.id === currentItem.id,
            )
            return original?.enabled !== currentItem.enabled
          }),
        }
      },
    },
    watch: {
      "maintenanceConfig.data": {
        immediate: true,
        handler(newConfig) {
          if (newConfig) {
            this.updateConfigurationFromData(newConfig)
          }
        },
      },
      "maintenanceConfigMutator.isSuccess": {
        immediate: true,
        handler(newVal) {
          if (newVal) {
            this.handleSaveSuccess()
          }
        },
      },
    },
    methods: {
      openSaveModal() {
        this.maintenanceConfigMutator.reset()
        this.showSaveModal = true
      },
      createConfigSnapshot() {
        return {
          isMaintenanceBannerEnabled: this.isMaintenanceBannerEnabled,
          title: this.title,
          message: this.message,
          items: cloneDeep(this.items),
        }
      },
      handleSaveSuccess() {
        this.configCopy = this.createConfigSnapshot()
      },
      resetChanges() {
        const { isMaintenanceBannerEnabled, title, message, items } =
          this.configCopy

        this.isMaintenanceBannerEnabled = isMaintenanceBannerEnabled
        this.title = title
        this.message = message
        this.items = cloneDeep(items)
      },
      getEnabledPages() {
        return this.items
          .filter((item) => item.enabled)
          .map((item) => item.name)
      },
      saveMaintenanceConfig() {
        const enabledPages = this.getEnabledPages()

        this.maintenanceConfigMutator.mutate({
          isMaintenanceBannerEnabled: this.isMaintenanceBannerEnabled,
          maintenanceBannerContent: {
            title: this.title,
            message: this.message,
            pages: enabledPages,
          },
        })
      },
      updateConfigurationFromData(config) {
        const { isMaintenanceBannerEnabled, maintenanceBannerContent } = config
        const {
          title = "",
          message = "",
          pages = [],
        } = maintenanceBannerContent || {}
        this.isMaintenanceBannerEnabled = !!isMaintenanceBannerEnabled
        this.title = title
        this.message = message
        this.items = AVAILABLE_PAGES.map((page, index) => ({
          id: index + 1,
          name: page,
          enabled: pages.includes(page),
        }))
        this.configCopy = this.createConfigSnapshot()
      },
    },
  })
</script>
